import { Injectable, computed, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  DEFAULT_WHATS_NEW_THEME, WhatsNewEntry, WhatsNewHeaderTheme, coerceHeaderTheme,
} from '../models/whats-new.model';
import {
  WHATS_NEW_CHANGELOG_URL, WHATS_NEW_ENTRIES, WHATS_NEW_RELEASE_LABEL,
} from '../config/whats-new-entries';
import { BackendService } from './backend.service';

const STORAGE_PREFIX = 'wg-whats-new-seen';

/** GET /agenthome/whats-new response body (admin-authored via the Admin portal). */
interface WhatsNewDoc {
  entries: Array<{
    id: string; icon: string; title: string; description: string; date: string;
    navBadge?: string;
    featured?: boolean;
    imageUrl?: string;
    cta?: { label: string; route?: string; actionId?: string };
  }>;
  releaseLabel: string;
  changelogUrl: string;
  /** Curated banner theme key (admin-picked); unknown/empty → ember. */
  headerTheme?: string;
}

/**
 * State + persistence for the What's New panel (the panel itself lives in the
 * layout shell, mirroring AbilitiesModalComponent, so any surface can open it).
 *
 * Announcements are ADMIN-AUTHORED: fetched once per session from
 * GET /agenthome/whats-new (managed in the Admin portal), so launching a
 * feature is an admin action, not a UI deploy. The bundled
 * WHATS_NEW_ENTRIES config is only the fallback for when the endpoint is
 * unreachable (pre-rollout / outage). Which entries a user has already seen is
 * kept in localStorage, namespaced by the signed-in email, so the panel
 * auto-opens exactly once per user per batch of announcements.
 *
 * Auto-open contract: the home screen calls maybeAutoOpen() once per visit
 * (and again right after the first-run tour ends) — if the document is still
 * loading, the intent is remembered and honored when it lands. The panel shows
 * only unseen entries. The sparkle button calls openAll() to re-read
 * everything anytime.
 *
 * Closing vs acknowledging are DIFFERENT acts: close() (× / Escape) leaves
 * seen-state untouched so an unread launch survives a reflexive dismiss;
 * dismiss() ("Got it") is the deliberate acknowledgement that marks entries
 * seen and lets the nav "New" pills expire.
 */
@Injectable({ providedIn: 'root' })
export class WhatsNewService {
  private readonly _seenIds = signal<ReadonlySet<string>>(this.readSeen());
  private readonly _open = signal(false);
  /** false = only unseen entries (auto-open); true = full registry (manual). */
  private readonly _showAll = signal(false);
  /** null until the server document (or the fallback) has resolved. */
  private readonly _entries = signal<WhatsNewEntry[] | null>(null);
  private readonly _releaseLabel = signal(WHATS_NEW_RELEASE_LABEL);
  private readonly _changelogUrl = signal(WHATS_NEW_CHANGELOG_URL);
  private readonly _headerTheme = signal<WhatsNewHeaderTheme>(DEFAULT_WHATS_NEW_THEME);
  /** An auto-open that arrived before the document loaded; replayed on load. */
  private _autoOpenPending = false;

  readonly open = this._open.asReadonly();
  readonly showingAll = this._showAll.asReadonly();
  readonly releaseLabel = this._releaseLabel.asReadonly();
  readonly changelogUrl = this._changelogUrl.asReadonly();
  readonly headerTheme = this._headerTheme.asReadonly();

  readonly unseen = computed(() =>
    this.sorted().filter(e => !this._seenIds().has(e.id)));
  readonly hasUnseen = computed(() => this.unseen().length > 0);

  /** The entries the panel should currently render. */
  readonly entries = computed(() =>
    this._showAll() ? this.sorted() : this.unseen());

  /** The one entry to spotlight, or null. Only the NEWEST flagged entry wins —
   *  `entries` is date-sorted, so an admin who leaves several flagged still
   *  gets one hero and a digest, never a stack of heroes. */
  readonly featured = computed<WhatsNewEntry | null>(() =>
    this.entries().find(e => e.featured) ?? null);

  /** Everything the spotlight didn't take, in the same order. */
  readonly digest = computed<WhatsNewEntry[]>(() => {
    const hero = this.featured();
    return hero ? this.entries().filter(e => e.id !== hero.id) : this.entries();
  });

  constructor(
    private readonly http: HttpClient,
    private readonly backend: BackendService,
  ) {
    this.load();
  }

  /** True while any unseen entry declares this nav-badge anchor — drives the
   *  little "New" pill on nav items, which self-expires once the announcement
   *  is seen. Nothing shows until the document has loaded (no badge flash). */
  hasUnseenBadge(anchor: string): boolean {
    return this.unseen().some(e => e.navBadge === anchor);
  }

  /** True until the user has dismissed a panel containing this entry — the
   *  modal shows a "New" pill on such rows when re-reading everything. */
  isUnseen(id: string): boolean {
    return !this._seenIds().has(id);
  }

  /** Home-screen hook: open once when something unseen shipped. Safe to call
   *  before the document loads — the intent is replayed when it lands. */
  maybeAutoOpen(): boolean {
    if (this._entries() === null) {
      this._autoOpenPending = true;
      return false;
    }
    // "Not now" holds for the rest of the session. Without this, close() no
    // longer marking entries seen would make the panel re-pop on every return
    // to the home screen — trading a destructive dismiss for a nag.
    if (this.isAutoOpenDeferred()) return false;
    if (!this.hasUnseen() || this._open()) return false;
    this._showAll.set(false);
    this._open.set(true);
    return true;
  }

  /** Re-fetch from the server, then auto-open if something is unseen. Called on
   *  each home visit so the panel never shows a stale boot-time snapshot (the
   *  service is a root singleton that otherwise fetches only once at startup —
   *  a session that booted before an announcement was published would never
   *  see it without this). */
  refreshThenMaybeAutoOpen(): void {
    this.load(true);
  }

  /** Manual entry point (sparkle button): re-fetch so the panel reflects the
   *  latest published announcements, and show everything (seen included). */
  openAll(): void {
    this._showAll.set(true);
    this._open.set(true);
    this.load(false);
  }

  /** Close WITHOUT consuming anything — × and Escape.
   *
   *  The panel auto-opens over the chat while people are mid-task, so × is a
   *  reflex. It used to mark every unseen entry seen (and clear the nav "New"
   *  pills with them), which meant one reflexive close could silently burn a
   *  launch nobody had read. Closing now means "not now"; only the explicit
   *  acknowledgement below means "I've read these". */
  close(): void {
    this._open.set(false);
    this.deferAutoOpen();
  }

  /** Acknowledge — "Got it". Closes AND marks everything on screen as seen. */
  dismiss(): void {
    this._open.set(false);
    this.markSeen(this.entries().map(e => e.id));
  }

  /** Acting on one entry's CTA acknowledges THAT entry only — the strongest
   *  possible read signal for it, and none at all for the others. */
  acknowledgeOne(id: string): void {
    this.markSeen([id]);
  }

  /** Fetch the admin-authored document; bundled entries only on failure.
   *  @param autoOpenAfter when true, attempt the once-per-user auto-open once
   *  the fresh document has landed (used by refreshThenMaybeAutoOpen). */
  private load(autoOpenAfter = false): void {
    const url = this.backend.createAgentsUrl('agenthome/whats-new');
    this.http.get<{ success: boolean; message: string; body: WhatsNewDoc | null }>(url)
      .subscribe({
        next: res => {
          const doc = res?.body;
          this._entries.set((doc?.entries ?? []).map(e => ({
            id: e.id, icon: e.icon || 'pi pi-sparkles', title: e.title,
            description: e.description, date: e.date || '',
            navBadge: e.navBadge || undefined,
            featured: !!e.featured,
            imageUrl: e.imageUrl || undefined,
            cta: e.cta?.label
              ? { label: e.cta.label, route: e.cta.route || undefined, actionId: e.cta.actionId || undefined }
              : undefined,
          })));
          this._releaseLabel.set(doc?.releaseLabel ?? '');
          this._changelogUrl.set(doc?.changelogUrl ?? '');
          this._headerTheme.set(coerceHeaderTheme(doc?.headerTheme));
          this.flushPendingAutoOpen();
          if (autoOpenAfter) this.maybeAutoOpen();
        },
        error: () => {
          // Endpoint absent (pre-rollout) or down — the small bundled registry
          // keeps the panel functional; config consts keep title/link behavior.
          this._entries.set([...WHATS_NEW_ENTRIES]);
          this.flushPendingAutoOpen();
          if (autoOpenAfter) this.maybeAutoOpen();
        },
      });
  }

  private flushPendingAutoOpen(): void {
    if (!this._autoOpenPending) return;
    this._autoOpenPending = false;
    this.maybeAutoOpen();
  }

  private sorted(): WhatsNewEntry[] {
    return [...(this._entries() ?? [])].sort((a, b) => b.date.localeCompare(a.date));
  }

  private markSeen(ids: string[]): void {
    if (ids.length === 0) return;
    const next = new Set(this._seenIds());
    ids.forEach(id => next.add(id));
    this._seenIds.set(next);
    try {
      localStorage.setItem(this.storageKey(), JSON.stringify([...next]));
    } catch { /* storage unavailable */ }
  }

  /** Remember "not now" for this browser session only.
   *
   *  sessionStorage, not localStorage, on purpose: the entries are still
   *  UNSEEN, so the nav "New" pills stay lit and the panel still lists them as
   *  new — deferring only stops the unprompted pop-up. A genuinely unread
   *  launch gets one more chance next session, and "Got it" ends it for good. */
  private deferAutoOpen(): void {
    try { sessionStorage.setItem(this.deferKey(), '1'); } catch { /* storage unavailable */ }
  }

  private isAutoOpenDeferred(): boolean {
    try { return sessionStorage.getItem(this.deferKey()) === '1'; } catch { return false; }
  }

  private deferKey(): string {
    return `${this.storageKey()}::deferred`;
  }

  private readSeen(): ReadonlySet<string> {
    try {
      const raw = localStorage.getItem(this.storageKey());
      const ids = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(ids) ? ids.filter(x => typeof x === 'string') : []);
    } catch {
      return new Set();
    }
  }

  /** Seen-state is per user: key on the signed-in email (login precedes any
   *  surface that injects this service, so the email is already in place). */
  private storageKey(): string {
    let email = '';
    try { email = (sessionStorage.getItem('current-email') ?? '').toLowerCase(); } catch { /* noop */ }
    return `${STORAGE_PREFIX}::${email || 'anon'}`;
  }
}
